// FolderUploadButton.js
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// import UploadChooser from "./UploadChooser";

function bytes(n) {
    if (!Number.isFinite(n)) return "";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0, v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    const p = v < 10 && i > 0 ? 1 : 0;
    return `${v.toFixed(p)} ${u[i]}`;
}

// Простенький модал без бібліотек
function Modal({ open, title, children, onClose }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/40" onClick={onClose} />
            <div className="relative z-10 w-full max-w-lg rounded-xl border bg-white p-4 shadow-2xl dark:bg-neutral-900">
                <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-sm font-semibold">{title}</h2>
                    <button
                        className="rounded border px-2 py-1 text-xs"
                        onClick={onClose}
                        type="button"
                    >
                        ✕
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

export default function FolderUploadButton({
    uploadLb,
    authed = true,
    className = "",
    showFileProgress = false,
}) {
    const [open, setOpen] = React.useState(false);
    const containerRef = useRef(null);
    const filesRef = React.useRef(null);
    const dirRef = React.useRef(null);

    const disabled = !authed || uploadLb?.uploading;
    const overall = uploadLb?.overall || {};
    const totalFiles = overall?.total ?? 0;
    const doneFiles = overall?.done ?? 0;
    const sent = overall?.bytesSent ?? 0;
    const total = overall?.bytesTotal ?? 0;

    const pct = total
        ? Math.min(100, Math.round((sent / total) * 100))
        : (totalFiles ? Math.min(100, Math.round((doneFiles / totalFiles) * 100)) : 0);

    const currentFolderName = uploadLb?.path?.[uploadLb.path.length - 1]?.name || "—";

    // ==== збір помилок і діалог збереження списку ====
    const [failedList, setFailedList] = useState([]); // масив рядків-шляхів
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const prevUploadingRef = useRef(uploadLb?.uploading);

    const listWrapRef = React.useRef(null);  // контейнер зі скролом (тільки UL)
    const fileProgKeys = Object.keys(uploadLb?.fileProgress || {});

    // автоскрол у кінець списку при зміні переліку елементів/прогресу
    React.useEffect(() => {
        const el = listWrapRef.current;
        if (!el) return;
        // проскролити в самий низ
        el.scrollTop = el.scrollHeight;
    }, [fileProgKeys.length, uploadLb?.fileProgress]);

    // коли аплоад завершився — перевіряємо помилки
    useEffect(() => {
        const prevUploading = prevUploadingRef.current;
        const nowUploading = !!uploadLb?.uploading;
        prevUploadingRef.current = nowUploading;

        // перехід true -> false
        if (prevUploading && !nowUploading) {
            const fp = uploadLb?.fileProgress || {};
            const failed = Object.entries(fp)
                .filter(([, v]) => (v?.status === "error"))
                .map(([k]) => k); // k — це relPath, який ми використовуємо ключем
            if (failed.length > 0) {
                setFailedList(failed);
                setShowFailedDialog(true);
            } else {
                setFailedList([]);
                setShowFailedDialog(false);
            }
        }
    }, [uploadLb?.uploading, uploadLb?.fileProgress]);

    // збереження файлу зі списком помилок
    async function saveFailedListAsTxt(list) {
        try {
            const text = list.join("\n");
            const defaultName = `failed-uploads-${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;

            // Якщо доступний File System Access API
            if ("showSaveFilePicker" in window) {
                const handle = await window.showSaveFilePicker({
                    suggestedName: defaultName,
                    types: [
                        {
                            description: "Text file",
                            accept: { "text/plain": [".txt"] },
                        },
                    ],
                });
                const writable = await handle.createWritable();
                await writable.write(text);
                await writable.close();
                return;
            }

            // Фолбек: створюємо Blob та ініціюємо download
            const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = defaultName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            // можна підкинути toast/alert у твоїй системі
            console.error("Save failed list error:", e);
            alert("Не вдалося зберегти список. Спробуйте ще раз або дозвольте доступ до файлової системи.");
        }
    }

    const failedCount = failedList.length;

    const onFilesChange = (e) => {
        if (e.target.files?.length) uploadLb.uploadFiles(e.target.files);
        e.target.value = "";
    };

    const onDirChange = (e) => {
        if (e.target.files?.length) uploadLb.uploadFiles(e.target.files);
        e.target.value = "";
    };

    // закриття по кліку поза меню / Escape
    useEffect(() => {
        if (!open) return;
        const onDown = (ev) => {
            // закриваємо тільки якщо клік поза всім блоком кнопка+меню
            if (containerRef.current && !containerRef.current.contains(ev.target)) {
                setOpen(false);
            }
        };
        const onKey = (ev) => { if (ev.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown, true);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("mousedown", onDown, true);
            document.removeEventListener("keydown", onKey, true);
        };
    }, [open]);

    return (
        <div ref={containerRef} className={`relative inline-flex flex-col gap-2 ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={disabled}
                className={[
                    "group relative inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
                    "shadow-sm transition active:scale-[.98] focus:outline-none focus:ring-2 focus:ring-blue-500/50",
                    disabled
                        ? "cursor-not-allowed opacity-60"
                        : "hover:bg-gray-50 dark:hover:bg-neutral-800 cursor-pointer",
                ].join(" ")}
                aria-busy={uploadLb?.uploading ? "true" : "false"}
                aria-label={`Завантажити нові папки з файлами у ${currentFolderName}`}
            >
                <span className="truncate">
                    Завантажити у <b className="font-semibold">{currentFolderName}</b>
                </span>

                {uploadLb?.uploading ? (
                    <span
                        className="ml-1 inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300"
                        aria-live="polite"
                    >
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                            <path className="opacity-90" d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="3" />
                        </svg>
                        {pct}% • {bytes(sent)} / {bytes(total)}
                    </span>
                ) : null}

                {uploadLb?.uploading && (
                    <span
                        className="pointer-events-none absolute inset-x-0 -bottom-[2px] h-[2px] overflow-hidden rounded-b-md bg-gray-200"
                        aria-hidden
                    >
                        <span
                            className="block h-full bg-blue-600 transition-[width]"
                            style={{ width: `${pct}%` }}
                        />
                    </span>
                )}
            </button>

            {/* приховані інпути */}
            <input
                ref={filesRef}
                type="file"
                multiple
                className="hidden"
                onChange={onFilesChange}
                disabled={disabled}
            />
            <input
                ref={dirRef}
                type="file"
                multiple
                webkitdirectory=""
                className="hidden"
                onChange={onDirChange}
                disabled={disabled}
            />
            {open && (
                <div
                    role="menu"
                    className="absolute z-50 mt-10 w-56 overflow-hidden rounded-lg border bg-white shadow-lg dark:bg-neutral-900"
                >
                    <button
                        role="menuitem"
                        type="button"
                        onClick={() => {
                            if (disabled) return;
                            setOpen(false);
                            setTimeout(() => filesRef.current?.click(), 0);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-neutral-800"
                        disabled={disabled}
                    >
                        <span aria-hidden>📄</span>
                        <span>Файли…</span>
                    </button>

                    <button
                        role="menuitem"
                        type="button"
                        onClick={() => {
                            if (disabled) return;
                            setOpen(false);
                            setTimeout(async () => {
                                if ('showDirectoryPicker' in window && typeof uploadLb.pickFolderAdvanced === 'function') {
                                    try { await uploadLb.pickFolderAdvanced(); } catch { /* */ }
                                } else {
                                    dirRef.current?.click();
                                }
                            }, 0);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-neutral-800"
                        disabled={disabled}
                    >
                        <span aria-hidden>📁</span>
                        <span>Папку…</span>
                    </button>
                </div>
            )}


            {showFileProgress && uploadLb?.uploading && (
                <div
                    className={[
                        "absolute z-50 mt-2 w-[min(92vw,560px)]",
                        "left-0", // або right-0 — якщо треба приставити праворуч
                    ].join(" ")}
                    style={{ top: "calc(100% + 4px)" }}  // нижче кнопки
                >
                    <div className="rounded-lg border bg-white text-xs shadow-xl dark:bg-neutral-900">
                        {/* ШАПКА — ЗАВЖДИ ВИДИМА */}
                        <div className="sticky top-0 z-10 mb-0 flex items-center justify-between gap-2 border-b bg-white px-2 py-2 dark:bg-neutral-900">
                            <div className="text-gray-600 dark:text-gray-300">
                                Файлів: {doneFiles}/{totalFiles} • {bytes(sent)} / {bytes(total)}
                            </div>
                            <div className="text-gray-500">{pct}%</div>
                        </div>

                        {/* СКРОЛЬОВАНИЙ СПИСОК. Автоскрол — через useEffect і ref */}
                        <div ref={listWrapRef} className="max-h-56 w-full overflow-auto p-2">
                            <ul className="space-y-1">
                                {Object.entries(uploadLb?.fileProgress || {}).map(([key, p]) => {
                                    const fpct = Math.min(100, Math.max(0, Math.round(p?.percent ?? 0)));
                                    const status = p?.status || (fpct >= 100 ? "done" : "uploading");
                                    return (
                                        <li key={key} className="rounded-md border p-2">
                                            <div className="flex items-center justify-between">
                                                <span className="truncate pr-2" title={key}>{key}</span>
                                                <span className="shrink-0 text-gray-500">{fpct}%</span>
                                            </div>
                                            <div className="mt-1 h-1.5 w-full rounded bg-gray-200">
                                                <div
                                                    className={"h-1.5 rounded " + (status === "error" ? "bg-red-600" : "bg-blue-600")}
                                                    style={{ width: `${fpct}%` }}
                                                />
                                            </div>
                                            <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
                                                <span>{bytes(p?.loaded ?? 0)} / {bytes(p?.total ?? 0)}</span>
                                                <span>{status === "done" ? "Готово" : status === "error" ? "Помилка" : "Йде…"}</span>
                                            </div>
                                        </li>
                                    );
                                })}
                                {Object.keys(uploadLb?.fileProgress || {}).length === 0 && (
                                    <li className="rounded-md border p-2 text-gray-500">Очікуємо прогрес…</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* ==== NEW: Діалог збереження списку помилкових файлів ==== */}
            <Modal
                open={showFailedDialog}
                title="Деякі файли не вдалося завантажити"
                onClose={() => setShowFailedDialog(false)}
            >
                <p className="text-sm text-gray-700 dark:text-gray-200">
                    Кількість помилок: <b>{failedCount}</b>
                </p>
                <div className="mt-2 max-h-48 overflow-auto rounded border bg-gray-50 p-2 text-xs dark:bg-neutral-800">
                    <ul className="list-disc pl-4">
                        {failedList.map((p) => <li key={p} className="break-all">{p}</li>)}
                    </ul>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        className="rounded border px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-neutral-800"
                        onClick={() => setShowFailedDialog(false)}
                    >
                        Закрити
                    </button>
                    <button
                        type="button"
                        className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                        onClick={async () => {
                            await saveFailedListAsTxt(failedList);
                            setShowFailedDialog(false);
                        }}
                    >
                        Зберегти список…
                    </button>
                </div>
            </Modal>
        </div >
    );
}

Modal.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string,
    children: PropTypes.node,     // ⬅️ це прибере помилку про children
    onClose: PropTypes.func.isRequired,
};

Modal.defaultProps = {
    title: "",
};

FolderUploadButton.propTypes = {
    uploadLb: PropTypes.shape({
        pickFolderAdvanced: PropTypes.func.isRequired,
        uploadFiles: PropTypes.func,
        uploading: PropTypes.bool,
        overall: PropTypes.shape({
            done: PropTypes.number,
            total: PropTypes.number,
            bytesSent: PropTypes.number,
            bytesTotal: PropTypes.number,
        }),
        fileProgress: PropTypes.object, // { [relPath]: {loaded,total,percent,status} }
        path: PropTypes.array,
    }).isRequired,
    authed: PropTypes.bool,
    className: PropTypes.string,
    showFileProgress: PropTypes.bool,
    showChooser: PropTypes.bool,
};
